import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
    baseURL: `${BACKEND_URL}/api`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth APIs
export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me'),
};

// Package APIs
export const packageAPI = {
    getActive: () => api.get('/packages'),
    getAll: () => api.get('/packages/all'),
    create: (data) => api.post('/packages', data),
    update: (id, data) => api.put(`/packages/${id}`, data),
    delete: (id) => api.delete(`/packages/${id}`),
};

// Category APIs
export const categoryAPI = {
    getActive: () => api.get('/categories'),
    getAll: () => api.get('/categories/all'),
    create: (data) => api.post('/categories', data),
    update: (id, data) => api.put(`/categories/${id}`, data),
    delete: (id) => api.delete(`/categories/${id}`),
};

// Background APIs
export const backgroundAPI = {
    getAll: (categoryId) => api.get('/backgrounds', { params: { category_id: categoryId } }),
    create: (data) => api.post('/backgrounds', data),
    update: (id, data) => api.put(`/backgrounds/${id}`, data),
    delete: (id) => api.delete(`/backgrounds/${id}`),
};

// Model Artist APIs
export const modelAPI = {
    getActive: () => api.get('/models'),
    getAll: () => api.get('/models/all'),
    create: (data) => api.post('/models', data),
    update: (id, data) => api.put(`/models/${id}`, data),
    delete: (id) => api.delete(`/models/${id}`),
};

// Style APIs
export const styleAPI = {
    getAll: (categoryId) => api.get('/styles', { params: { category_id: categoryId } }),
    create: (data) => api.post('/styles', data),
    update: (id, data) => api.put(`/styles/${id}`, data),
    delete: (id) => api.delete(`/styles/${id}`),
};

// Gallery APIs
export const galleryAPI = {
    getAll: (skip = 0, limit = 50) => api.get('/gallery', { params: { skip, limit } }),
    getById: (id) => api.get(`/gallery/${id}`),
    delete: (id) => api.delete(`/gallery/${id}`),
};

// Generation APIs
export const generateAPI = {
    calculateTokens: (data) => api.post('/generate/calculate-tokens', data),
    generate: (data) => api.post('/generate', data),
};

// Payment APIs
export const paymentAPI = {
    create: (packageId, redirectUrl) => api.post('/payments/create', null, { 
        params: { package_id: packageId, redirect_url: redirectUrl } 
    }),
};

// User Management APIs (Admin)
export const userAPI = {
    getAll: () => api.get('/users'),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
};

// Settings APIs (Admin)
export const settingsAPI = {
    get: () => api.get('/settings'),
    update: (data) => api.put('/settings', data),
};

// Dashboard APIs (Admin)
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
};

// Upload API
export const uploadAPI = {
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
};

// Helper function to get proper image URL with proxy
export const getImageUrl = (url) => {
    if (!url) return '';
    // If already a full URL or starts with /api/files, use as is
    if (url.startsWith('http') || url.startsWith('/api/files/')) {
        // For absolute URLs from backend, prepend BACKEND_URL if needed
        if (url.startsWith('/api/files/')) {
            return `${BACKEND_URL}${url}`;
        }
        return url;
    }
    // For MinIO paths, route through proxy
    return `${BACKEND_URL}/api/files/${url}`;
};

export default api;
